import type { CellObject, Sheet } from "write-excel-file/browser";

export const EXCEL_MAX_ROWS = 1_048_576;
export const EXCEL_MAX_DATA_ROWS = EXCEL_MAX_ROWS - 1;
export const EXCEL_MAX_COLUMNS = 16_384;
export const EXCEL_MAX_CELL_CHARACTERS = 32_767;

export type ReportColumnType =
  | "string"
  | "date"
  | "datetime"
  | "currency"
  | "number"
  | "percent"
  | "boolean";

export interface ReportColumn {
  key: string;
  label: string;
  type?: ReportColumnType;
  percentScale?: "whole" | "fraction";
  width?: number;
  format?: string;
  wrap?: boolean;
}

export type ReportRow = Record<string, unknown> | unknown[];

export interface ReportSheet {
  name: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  total?: number;
  notice?: string;
}

export interface ReportWorkbookInput {
  title: string;
  fileName?: string;
  generatedAt: string | Date;
  requestedBy: string;
  timezone: string;
  filters?: Record<string, unknown>;
  sheets: ReportSheet[];
}

export interface ReportCsvInput {
  title?: string;
  fileName?: string;
  columns: ReportColumn[];
  rows: ReportRow[];
}

export interface ReportFile {
  blob: Blob;
  fileName: string;
}

type ExcelCell = CellObject;
type ExcelSheet = Sheet<File | Blob | ArrayBuffer>;

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const CSV_MIME = "text/csv;charset=utf-8";
const DEFAULT_TIMEZONE = "America/Vancouver";
const DETAILS_SHEET_NAME = "Report Details";
const BRAND_NAVY = "#294A60";
const BRAND_NAVY_DARK = "#1F394A";
const BRAND_ORANGE = "#C47A2C";
const BRAND_GREEN = "#4F9865";
const BODY_TEXT = "#26343D";
const MUTED_TEXT = "#65737D";
const LIGHT_BLUE = "#EAF0F4";
const LIGHT_ROW = "#F7F9FA";
const BORDER = "#D7DEE2";
const DEFAULT_CURRENCY_FORMAT = "$#,##0.00;[Red]($#,##0.00);-";
const DEFAULT_NUMBER_FORMAT = "#,##0.00;[Red](#,##0.00);-";
const DEFAULT_PERCENT_FORMAT = "0.0%;[Red](0.0%);-";
const DEFAULT_DATE_FORMAT = "yyyy-mm-dd";
const DEFAULT_DATETIME_FORMAT = "yyyy-mm-dd hh:mm";
const INVALID_XML_CONTROLS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;
const ILLEGAL_SHEET_CHARACTERS = /[\[\]\\/:*?]/g;
const ILLEGAL_FILE_CHARACTERS = /[<>:"/\\|?*\u0000-\u001F]/g;
const WINDOWS_RESERVED_BASENAME = /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i;
const CSV_FORMULA_PREFIX = /^[\t\r\n ]*[=+\-@]/;

function cleanText(value: unknown) {
  return String(value ?? "").replace(INVALID_XML_CONTROLS, "");
}

function boundedText(value: unknown, context: string) {
  const text = cleanText(value);
  if (text.length > EXCEL_MAX_CELL_CHARACTERS) {
    throw new Error(`${context} exceeds Excel's ${EXCEL_MAX_CELL_CHARACTERS.toLocaleString("en-CA")}-character cell limit.`);
  }
  return text;
}

function validDate(value: string | Date) {
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) throw new Error("generatedAt must be a valid date or ISO timestamp.");
  return date;
}

function validTimeZone(value: unknown) {
  const timezone = cleanText(value).trim() || DEFAULT_TIMEZONE;
  try {
    new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(new Date());
    return timezone;
  } catch {
    throw new Error(`Invalid report timezone: ${timezone}`);
  }
}

function datePartsInZone(date: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);
  return Object.fromEntries(parts.filter(part => part.type !== "literal").map(part => [part.type, part.value]));
}

function excelDateAtBusinessTime(value: unknown, timezone: string, dateOnly: boolean) {
  if (value == null || value === "") return null;

  if (dateOnly && typeof value === "string") {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      const date = new Date(Date.UTC(year, month - 1, day));
      if (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day) return date;
    }
  }

  const date = value instanceof Date ? new Date(value.getTime()) : new Date(String(value));
  if (Number.isNaN(date.getTime())) return null;
  const parts = datePartsInZone(date, timezone);
  return new Date(Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    dateOnly ? 0 : Number(parts.hour),
    dateOnly ? 0 : Number(parts.minute),
    dateOnly ? 0 : Number(parts.second)
  ));
}

function localizedTimestamp(value: string | Date, timezone: string) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
    timeZoneName: "short"
  }).format(validDate(value));
}

function ymd(value: string | Date) {
  return validDate(value).toISOString().slice(0, 10);
}

function safeBaseName(value: unknown, fallback: string) {
  const finalSegment = cleanText(value).split(/[\\/]/).pop() || "";
  let name = finalSegment
    .replace(ILLEGAL_FILE_CHARACTERS, "-")
    .replace(/\s+/g, " ")
    .replace(/[. ]+$/g, "")
    .trim();
  if (!name) name = fallback;
  if (WINDOWS_RESERVED_BASENAME.test(name)) name = `ranch-power-${name}`;
  return name.slice(0, 120).replace(/[. ]+$/g, "") || fallback;
}

function withExtension(value: unknown, extension: "xlsx" | "csv", fallback: string) {
  const withoutExtension = cleanText(value).replace(/\.(?:xlsx|xls|csv)$/i, "");
  return `${safeBaseName(withoutExtension, fallback)}.${extension}`;
}

function slug(value: unknown) {
  const normalized = cleanText(value)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
  return normalized || "operations-report";
}

function safeWorkbookFileName(input: ReportWorkbookInput) {
  const fallback = `ranch-power-${slug(input.title)}-${ymd(input.generatedAt)}`;
  return withExtension(input.fileName, "xlsx", fallback);
}

function safeCsvFileName(input: ReportCsvInput) {
  const fallback = `ranch-power-${slug(input.title || "operations-report")}-${new Date().toISOString().slice(0, 10)}`;
  return withExtension(input.fileName, "csv", fallback);
}

function safeSheetStem(value: unknown, fallback: string) {
  let name = cleanText(value)
    .replace(ILLEGAL_SHEET_CHARACTERS, " - ")
    .replace(/\s+/g, " ")
    .replace(/^'+|'+$/g, "")
    .trim();
  if (!name) name = fallback;
  if (/^history$/i.test(name)) name = "History Data";
  return name.slice(0, 31).trim() || fallback;
}

function uniqueSheetName(value: unknown, fallback: string, used: Set<string>) {
  const stem = safeSheetStem(value, fallback);
  let candidate = stem;
  let suffix = 2;
  while (used.has(candidate.toLowerCase())) {
    const label = ` (${suffix})`;
    candidate = `${stem.slice(0, Math.max(1, 31 - label.length)).trim()}${label}`;
    suffix += 1;
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

function normalizedWidth(column: ReportColumn) {
  const explicit = Number(column.width);
  if (Number.isFinite(explicit) && explicit > 0) return Math.min(60, Math.max(8, Math.round(explicit)));
  const labelLength = cleanText(column.label).length;
  if (["currency", "number", "percent"].includes(column.type || "")) return Math.min(18, Math.max(12, labelLength + 2));
  if (column.type === "date") return 14;
  if (column.type === "datetime") return 21;
  if (column.type === "boolean") return Math.min(14, Math.max(10, labelLength + 2));
  return Math.min(36, Math.max(12, labelLength + 2));
}

function validateColumns(columns: ReportColumn[], context: string) {
  if (!Array.isArray(columns) || !columns.length) throw new Error(`${context} must include at least one column.`);
  if (columns.length > EXCEL_MAX_COLUMNS) {
    throw new Error(`${context} has ${columns.length.toLocaleString("en-CA")} columns; Excel supports at most ${EXCEL_MAX_COLUMNS.toLocaleString("en-CA")}.`);
  }
  const keys = new Set<string>();
  columns.forEach((column, index) => {
    const key = cleanText(column?.key).trim();
    const label = boundedText(column?.label, `${context} column ${index + 1} label`).trim();
    if (!key) throw new Error(`${context} column ${index + 1} is missing a key.`);
    if (!label) throw new Error(`${context} column ${index + 1} is missing a label.`);
    if (keys.has(key)) throw new Error(`${context} contains the duplicate column key "${key}".`);
    keys.add(key);
  });
}

function validateRows(rows: ReportRow[], context: string, headerRows = 1) {
  if (!Array.isArray(rows)) throw new Error(`${context} rows must be an array.`);
  const maxDataRows = EXCEL_MAX_ROWS - headerRows;
  if (rows.length > maxDataRows) {
    const headerDescription = headerRows === 1 ? "one header row" : `${headerRows} header rows`;
    throw new Error(`${context} has ${rows.length.toLocaleString("en-CA")} data rows; Excel supports at most ${maxDataRows.toLocaleString("en-CA")} plus ${headerDescription}.`);
  }
}

function rawRowValue(row: ReportRow, column: ReportColumn, columnIndex: number) {
  return Array.isArray(row) ? row[columnIndex] : row?.[column.key];
}

function finiteNumber(value: unknown) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value.replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function booleanValue(value: unknown) {
  if (value === true || value === false) return value;
  if (value === 1 || value === "1" || String(value).toLowerCase() === "true") return true;
  if (value === 0 || value === "0" || String(value).toLowerCase() === "false") return false;
  return null;
}

function stringValue(value: unknown, context: string) {
  if (value == null) return "";
  if (typeof value === "string") return boundedText(value, context);
  if (value instanceof Date) return boundedText(value.toISOString(), context);
  if (typeof value === "object") {
    try {
      return boundedText(JSON.stringify(value), context);
    } catch {
      return boundedText(String(value), context);
    }
  }
  return boundedText(value, context);
}

function emptyCell() {
  return { value: "", type: String, textColor: BODY_TEXT } satisfies ExcelCell;
}

function dataCell(value: unknown, column: ReportColumn, timezone: string, context: string, rowIndex: number): ExcelCell {
  const type = column.type || "string";
  const stripe = rowIndex % 2 === 1 ? LIGHT_ROW : "#FFFFFF";
  const shared = {
    backgroundColor: stripe,
    borderColor: BORDER,
    borderStyle: "thin",
    fontFamily: "Arial",
    fontSize: 10,
    textColor: BODY_TEXT,
    alignVertical: "top" as const,
    wrap: column.wrap === true
  } satisfies ExcelCell;
  if (value == null || value === "") return { ...emptyCell(), ...shared };

  if (type === "date" || type === "datetime") {
    const date = excelDateAtBusinessTime(value, timezone, type === "date");
    if (!date) return { ...shared, value: stringValue(value, context), type: String, align: "left" };
    return {
      ...shared,
      value: date,
      type: Date,
      format: column.format || (type === "date" ? DEFAULT_DATE_FORMAT : DEFAULT_DATETIME_FORMAT),
      align: "right"
    };
  }

  if (["currency", "number", "percent"].includes(type)) {
    const number = finiteNumber(value);
    if (number == null) return { ...shared, value: stringValue(value, context), type: String, align: "left" };
    const defaultFormat = type === "currency"
      ? DEFAULT_CURRENCY_FORMAT
      : type === "percent"
        ? DEFAULT_PERCENT_FORMAT
        : DEFAULT_NUMBER_FORMAT;
    const excelNumber = type === "percent" && column.percentScale !== "fraction" ? number / 100 : number;
    return { ...shared, value: excelNumber, type: Number, format: column.format || defaultFormat, align: "right" };
  }

  if (type === "boolean") {
    const boolean = booleanValue(value);
    if (boolean == null) return { ...shared, value: stringValue(value, context), type: String, align: "left" };
    return { ...shared, value: boolean, type: Boolean, align: "center" };
  }

  // Explicitly use the shared-string cell type. A leading =, +, -, or @ is
  // therefore literal text in XLSX rather than a formula.
  return { ...shared, value: stringValue(value, context), type: String, align: "left" };
}

function headerCell(column: ReportColumn): ExcelCell {
  return {
    value: boundedText(column.label, `Column ${column.key} header`),
    type: String,
    backgroundColor: BRAND_NAVY,
    textColor: "#FFFFFF",
    fontFamily: "Arial",
    fontSize: 10,
    fontWeight: "bold",
    align: ["currency", "number", "percent"].includes(column.type || "") ? "right" : "left",
    alignVertical: "center",
    wrap: true,
    borderColor: BRAND_NAVY_DARK,
    borderStyle: "thin",
    height: 30
  };
}

function filterValue(value: unknown, context: string) {
  if (value == null || value === "") return "All";
  if (Array.isArray(value)) return boundedText(value.map(item => cleanText(item)).join(", ") || "All", context);
  if (value instanceof Date) return boundedText(value.toISOString(), context);
  if (typeof value === "object") return stringValue(value, context);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return boundedText(value, context);
}

function titleRow(value: string, columns = 4): Array<ExcelCell | null> {
  return [
    {
      value,
      type: String,
      columnSpan: columns,
      backgroundColor: BRAND_NAVY_DARK,
      textColor: "#FFFFFF",
      fontFamily: "Arial",
      fontSize: 18,
      fontWeight: "bold",
      align: "left",
      alignVertical: "center",
      height: 34
    },
    ...Array.from({ length: columns - 1 }, () => null)
  ];
}

function sectionRow(value: string, columns = 4): Array<ExcelCell | null> {
  return [
    {
      value,
      type: String,
      columnSpan: columns,
      backgroundColor: LIGHT_BLUE,
      textColor: BRAND_NAVY_DARK,
      fontFamily: "Arial",
      fontSize: 11,
      fontWeight: "bold",
      align: "left",
      alignVertical: "center",
      height: 24,
      bottomBorderColor: BRAND_ORANGE,
      bottomBorderStyle: "medium"
    },
    ...Array.from({ length: columns - 1 }, () => null)
  ];
}

function detailLabel(value: unknown, context = "Report detail label"): ExcelCell {
  return {
    value: boundedText(value, context),
    type: String,
    backgroundColor: LIGHT_ROW,
    textColor: MUTED_TEXT,
    fontFamily: "Arial",
    fontSize: 10,
    fontWeight: "bold",
    borderColor: BORDER,
    borderStyle: "thin",
    alignVertical: "top",
    wrap: true
  };
}

function detailValue(value: unknown, context: string): ExcelCell {
  return {
    value: filterValue(value, context),
    type: String,
    backgroundColor: "#FFFFFF",
    textColor: BODY_TEXT,
    fontFamily: "Arial",
    fontSize: 10,
    borderColor: BORDER,
    borderStyle: "thin",
    alignVertical: "top",
    wrap: true
  };
}

function reportDetailsSheet(input: ReportWorkbookInput, names: string[]): ExcelSheet {
  const filters = Object.entries(input.filters || {});
  const data: Array<Array<ExcelCell | null>> = [
    titleRow(boundedText(input.title, "Report title")),
    [
      {
        value: "Ranch Power Operations",
        type: String,
        columnSpan: 4,
        textColor: BRAND_GREEN,
        fontFamily: "Arial",
        fontSize: 11,
        fontWeight: "bold",
        height: 22
      },
      null,
      null,
      null
    ],
    sectionRow("Report information"),
    [detailLabel("Generated"), detailValue(localizedTimestamp(input.generatedAt, input.timezone), "Generated timestamp"), detailLabel("Time zone"), detailValue(input.timezone, "Report timezone")],
    [detailLabel("Requested by"), detailValue(input.requestedBy, "Requested by"), detailLabel("Data sheets"), detailValue(input.sheets.length, "Data sheet count")],
    [detailLabel("Exported rows"), detailValue(input.sheets.reduce((sum, sheet) => sum + sheet.rows.length, 0), "Exported row count"), detailLabel("Matching rows"), detailValue(input.sheets.reduce((sum, sheet) => sum + Math.max(sheet.rows.length, Number(sheet.total) || 0), 0), "Matching row count")],
    [detailValue("", "Spacer"), detailValue("", "Spacer"), detailValue("", "Spacer"), detailValue("", "Spacer")],
    sectionRow("Filters"),
    ...(filters.length
      ? filters.map(([key, value], index) => [
        detailLabel(key, `Filter ${index + 1} label`),
        { ...detailValue(value, `Filter ${index + 1} value`), columnSpan: 3 },
        null,
        null
      ] as Array<ExcelCell | null>)
      : [[detailLabel("Selection"), { ...detailValue("All available records", "Filter selection"), columnSpan: 3 }, null, null] as Array<ExcelCell | null>]),
    [detailValue("", "Spacer"), detailValue("", "Spacer"), detailValue("", "Spacer"), detailValue("", "Spacer")],
    sectionRow("Workbook contents"),
    [
      { ...detailLabel("Sheet"), backgroundColor: BRAND_NAVY, textColor: "#FFFFFF" },
      { ...detailLabel("Exported rows"), backgroundColor: BRAND_NAVY, textColor: "#FFFFFF" },
      { ...detailLabel("Matching rows"), backgroundColor: BRAND_NAVY, textColor: "#FFFFFF" },
      { ...detailLabel("Columns"), backgroundColor: BRAND_NAVY, textColor: "#FFFFFF" }
    ],
    ...input.sheets.map((sheet, index) => [
      detailValue(names[index], `Sheet ${index + 1} name`),
      detailValue(sheet.rows.length, `Sheet ${index + 1} exported rows`),
      detailValue(Math.max(sheet.rows.length, Number(sheet.total) || 0), `Sheet ${index + 1} matching rows`),
      detailValue(sheet.columns.length, `Sheet ${index + 1} columns`)
    ])
  ];

  return {
    data,
    sheet: DETAILS_SHEET_NAME,
    columns: [{ width: 22 }, { width: 38 }, { width: 22 }, { width: 25 }],
    stickyRowsCount: 2,
    orientation: "landscape",
    showGridLines: false,
    zoomScale: 0.95
  };
}

function dataSheet(sheet: ReportSheet, name: string, timezone: string, sheetIndex: number): ExcelSheet {
  validateColumns(sheet.columns, `Sheet ${sheetIndex + 1}`);
  const notice = sheet.notice == null ? "" : boundedText(sheet.notice, `Sheet ${sheetIndex + 1} notice`).trim();
  validateRows(sheet.rows, `Sheet ${sheetIndex + 1}`, notice ? 2 : 1);
  const data: Array<Array<ExcelCell | null>> = [
    ...(notice ? [[{
      value: notice,
      type: String,
      columnSpan: sheet.columns.length,
      backgroundColor: "#FCE9D7",
      textColor: "#8A351B",
      fontFamily: "Arial",
      fontSize: 12,
      fontWeight: "bold",
      align: "left" as const,
      alignVertical: "center" as const,
      wrap: true,
      height: 42
    } satisfies ExcelCell, ...Array.from({ length: sheet.columns.length - 1 }, () => null)]] : []),
    sheet.columns.map(headerCell),
    ...sheet.rows.map((row, rowIndex) => sheet.columns.map((column, columnIndex) => dataCell(
      rawRowValue(row, column, columnIndex),
      column,
      timezone,
      `${name} row ${rowIndex + (notice ? 3 : 2)}, ${column.label}`,
      rowIndex
    )))
  ];
  return {
    data,
    sheet: name,
    columns: sheet.columns.map(column => ({ width: normalizedWidth(column) })),
    stickyRowsCount: notice ? 2 : 1,
    ...(sheet.columns.length > 6 ? { orientation: "landscape" as const } : {}),
    showGridLines: false,
    zoomScale: sheet.columns.length > 12 ? 0.8 : 0.9
  };
}

function normalizedWorkbook(input: ReportWorkbookInput) {
  if (!input || typeof input !== "object") throw new Error("A report workbook is required.");
  if (!Array.isArray(input.sheets) || !input.sheets.length) throw new Error("A report workbook must include at least one data sheet.");
  const title = boundedText(input.title, "Report title").trim();
  const requestedBy = boundedText(input.requestedBy, "Requested by").trim();
  if (!title) throw new Error("A report title is required.");
  if (!requestedBy) throw new Error("The report requester is required.");
  const generatedAt = validDate(input.generatedAt);
  const timezone = validTimeZone(input.timezone);
  if (input.filters != null && (typeof input.filters !== "object" || Array.isArray(input.filters))) {
    throw new Error("Report filters must be a key/value object.");
  }
  const reportDetailsRows = 11 + Math.max(1, Object.keys(input.filters || {}).length) + input.sheets.length;
  if (reportDetailsRows > EXCEL_MAX_ROWS) {
    throw new Error(`Report Details would have ${reportDetailsRows.toLocaleString("en-CA")} rows; Excel supports at most ${EXCEL_MAX_ROWS.toLocaleString("en-CA")} rows per sheet.`);
  }
  const used = new Set<string>([DETAILS_SHEET_NAME.toLowerCase()]);
  const names = input.sheets.map((sheet, index) => uniqueSheetName(sheet?.name, `Report ${index + 1}`, used));
  const normalized: ReportWorkbookInput = { ...input, title, requestedBy, generatedAt, timezone };
  input.sheets.forEach((sheet, index) => {
    if (!sheet || typeof sheet !== "object") throw new Error(`Sheet ${index + 1} is invalid.`);
    validateColumns(sheet.columns, `Sheet ${index + 1}`);
    const notice = sheet.notice == null ? "" : boundedText(sheet.notice, `Sheet ${index + 1} notice`).trim();
    validateRows(sheet.rows, `Sheet ${index + 1}`, notice ? 2 : 1);
  });
  return { input: normalized, names };
}

export async function buildReportWorkbook(input: ReportWorkbookInput): Promise<ReportFile> {
  const normalized = normalizedWorkbook(input);
  const excelSheets: ExcelSheet[] = [
    reportDetailsSheet(normalized.input, normalized.names),
    ...normalized.input.sheets.map((sheet, index) => dataSheet(sheet, normalized.names[index], normalized.input.timezone, index))
  ];
  const module = await import("write-excel-file/browser");
  const blob = await module.default(excelSheets).toBlob();
  const typedBlob = blob.type === XLSX_MIME ? blob : new Blob([blob], { type: XLSX_MIME });
  return { blob: typedBlob, fileName: safeWorkbookFileName(normalized.input) };
}

function csvString(value: unknown, column: ReportColumn, context: string) {
  const type = column.type || "string";
  if (value == null) return "";
  if (["currency", "number", "percent"].includes(type)) {
    const number = finiteNumber(value);
    if (number != null) return String(number);
  }
  if (type === "boolean") {
    const boolean = booleanValue(value);
    if (boolean != null) return boolean ? "TRUE" : "FALSE";
  }
  if (type === "date") {
    const date = excelDateAtBusinessTime(value, DEFAULT_TIMEZONE, true);
    if (date) return date.toISOString().slice(0, 10);
  }
  if (type === "datetime") {
    const date = value instanceof Date ? value : new Date(String(value));
    if (!Number.isNaN(date.getTime())) return date.toISOString();
  }
  return formulaSafeCsvText(stringValue(value, context));
}

function formulaSafeCsvText(value: string) {
  return CSV_FORMULA_PREFIX.test(value) ? `'${value}` : value;
}

function csvCell(value: string) {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function buildReportCsv(input: ReportCsvInput): ReportFile & { text: string } {
  if (!input || typeof input !== "object") throw new Error("A CSV report is required.");
  validateColumns(input.columns, "CSV report");
  validateRows(input.rows, "CSV report");
  const header = input.columns.map(column => csvCell(formulaSafeCsvText(
    boundedText(column.label, `CSV column ${column.key} header`)
  ))).join(",");
  const rows = input.rows.map((row, rowIndex) => input.columns.map((column, columnIndex) => csvCell(csvString(
    rawRowValue(row, column, columnIndex),
    column,
    `CSV row ${rowIndex + 2}, ${column.label}`
  ))).join(","));
  const text = `\uFEFF${[header, ...rows].join("\r\n")}\r\n`;
  return {
    blob: new Blob([text], { type: CSV_MIME }),
    fileName: safeCsvFileName(input),
    text
  };
}

export function downloadReportBlob(blob: Blob, fileName: string) {
  if (!(blob instanceof Blob)) throw new Error("A report Blob is required.");
  if (typeof document === "undefined" || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
    throw new Error("Report downloads are only available in a browser.");
  }
  const extension = blob.type === XLSX_MIME ? "xlsx" : "csv";
  const safeName = withExtension(fileName, extension, `ranch-power-operations-report-${new Date().toISOString().slice(0, 10)}`);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = safeName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  return safeName;
}
